import { Injectable, Optional, SkipSelf } from '@angular/core';
import { SubscriptionManager } from '@sama/rxjs-core';
import { fromEvent, merge } from 'rxjs';
import { DebugLoggingService } from '../../core/shared/debug-logging/debug-logging.service';
import { AbstractTrackerWithContext } from '../../cross-team-shared/event-tracking/abstract-tracker-with-context';
import { TrackingEvent } from '../../cross-team-shared/event-tracking/event-utils';
import { BrowserControlService } from '../../cross-team-shared/misc-services/browser-control.service';
import { EventTrackingOptions } from '../../team-apis/core/event-tracking';
import { RootTrackerWrapper } from './root-tracker-wrapper';

/**
 * This service is provided in root, but also meant to be provided by components
 * that wish to add context to events originating from themselves or their
 * descendants. Simply add this to a components `providers` array to insert a
 * new level of the hierarchy that will be used when that component or its
 * descendants inject `EventTrackingService`.
 */
@Injectable()
export class EventTrackingService extends AbstractTrackerWithContext {
  constructor(
    private browserControlService: BrowserControlService,
    private debugLoggingService: DebugLoggingService,
    rootTrackerWrapper: RootTrackerWrapper,
    @Optional() @SkipSelf() parentFromDI?: EventTrackingService,
  ) {
    super(parentFromDI || rootTrackerWrapper);
  }

  override track(event: TrackingEvent, options?: EventTrackingOptions) {
    super.track(event, options);
    this.debugLoggingService.trackEvent(event, options);
  }

  subscribeToWorkspaceFocusEvents(subscriptionManager: SubscriptionManager) {
    subscriptionManager.subscribeTo(
      merge(fromEvent(window, 'focus'), fromEvent(window, 'blur')),
      () => {
        this.track(
          {
            id: 'change_workspace_focus',
            bucket: 'out_of_focus',
            workspace_focused: this.browserControlService.documentHasFocus(),
          },
          { isInteractive: false },
        );
      },
    );
  }
}
